/* eslint-disable react/prop-types */
import React, { createContext, useEffect, useState } from 'react'
import useAxios from 'axios-hooks'
import {
  ACCOUNT_API,
  LOGOUT_API,
  VALIDATE_SESSION,
  STATIC_PAGE,
  ECOMMERCE_API,
} from '../utils/apiUrls'
import { isEmpty } from 'lodash'
import i18n from 'i18next'
import { LG_SIZE, SECURITY_SESSION_ID, SM_SIZE } from '../utils/constants'
import { useTranslation } from 'react-i18next'
// import { useInterval } from '../hooks/useInterval'
import { captureGAEvent, raiseToast } from '../utils/helper'

const initialState = {
  userObj: {},
  isLogin: false,
  activePlans: [],
  userId: '',
  isCustomTemplate: false,
}
//create context

export const AppContext = createContext(initialState)

//Provider Component

export const AppProvider = ({ children }) => {
  const { t } = useTranslation()
  const [userObj, setUserObj] = useState({})
  const [accountObj, setAccountObj] = useState({})
  const [modulePermissions, setModulePermissions] = useState({})
  const [isLogin, setIsLogin] = useState(false)
  const [loader, setLoader] = useState(false)
  const [openLoginModal, setOpenLoginModal] = useState(false)
  const [openForgotPasswordModal, setOpenForgotPasswordModal] = useState(false)
  const [languageModal, setLanguageModal] = useState(false)
  const [verifyOtpModalData, setVerifyOtpModalData] = useState({})
  const [popupModalLink, setPopupModalLink] = useState('')
  const [tipJarModalConfig, setTipJarModalConfig] = useState({})
  const [popUpTitle, setPopUpTitle] = useState('')
  const [shareModalData, setShareModalData] = useState({})
  const [isAccountInactive, setIsAccountInactive] = useState(false)
  const [isCustomTemplate, setIsCustomTemplate] = useState(false)
  const [componentCustomForms, setComponentCustomForms] = useState([])
  const [staticPages, setStaticPages] = useState([])
  const [modalType, setModalType] = useState('login')
  const [isBrowser, setIsBrowser] = useState(true)
  const [openAreYouStillWatching, setOpenAreYouStillWatching] = useState(false)
  const [lastMouseMovedSec, setLastMouseMovedSec] = useState(0)
  const [userCart, setUserCart] = useState({
    products: [],
    total_discount: 0,
    total_payable: 0,
    total_price: 0,
  })

  const language = localStorage.getItem('language')

  // const [state, dispatch] = useReducer(AppReducer, initialState);

  // const [isAccess, setAccess] = useState(false)

  const handleResize = () => {
    if (window.innerWidth > LG_SIZE) {
      setIsBrowser(true)
    } else if (window.innerWidth < LG_SIZE && window.innerWidth > SM_SIZE) {
      setIsBrowser(false)
    } else {
      setIsBrowser(false)
    }
  }

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])
  const staticPageRoutes = {
    ABOUT_US: 'about-us',
    TNC: 'term-of-use',
    PRIVACY_POLICY: 'privacy-policy',
    COPYRIGHT: 'copyright',
    FAQ: 'faq',
  }

  useEffect(() => {
    const sessionId = localStorage.getItem(SECURITY_SESSION_ID)
    if (sessionId) {
      validateSessionCall()
      userCartCall()
    }
  }, [])

  const [{ data: staticRes }] = useAxios({
    url: STATIC_PAGE,
    method: 'get',
  })

  useEffect(() => {
    if (staticRes && staticRes.success) {
      const { data } = staticRes
      setStaticPages(
        data.filter((obj) => obj.visible_on_website === true && obj.type !== 'SOCIAL_LINKS'),
      )
    }
  }, [staticRes])

  const [{ data: userCartData }, userCartCall] = useAxios(
    {
      url: `${ECOMMERCE_API}/cart`,
      method: 'get',
    },
    { manual: true },
  )

  const [{ data: updateUserCartData }, updateUserCartCall] = useAxios(
    {
      url: `${ECOMMERCE_API}/cart`,
      method: 'put',
    },
    { manual: true },
  )

  const [{ data: accountData, loading: accountDataLoading }] = useAxios({
    url: ACCOUNT_API,
    method: 'get',
  })

  const [
    { data: validateSession, loading: validateSessionLoading, error: validateSessionError },
    validateSessionCall,
  ] = useAxios(
    {
      url: VALIDATE_SESSION,
      method: 'get',
    },
    { manual: true },
  )

  const [{ data: logout, loading: logoutLoading }, logoutCall] = useAxios(
    {
      url: LOGOUT_API,
      method: 'get',
    },
    { manual: true },
  )

  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language)
    }
  }, [])

  useEffect(() => {
    if (accountData && accountData.success) {
      if (accountData?.data?.status) {
        if (!language && !isEmpty(accountData?.data?.allowed_languages)) {
          localStorage.setItem('language', accountData?.data?.default_language)
          i18n.changeLanguage(accountData?.data?.default_language)
        }
        localStorage.setItem('default_language', accountData?.data?.default_language)
        localStorage.setItem('date_format', accountData?.data?.date_format)
        setAccountObj(accountData?.data)
        setIsCustomTemplate(accountData?.data?.od_resource_allowances?.custom_template)
        setModulePermissions(accountData?.data?.module_permissions)
      } else {
        setIsAccountInactive(true)
      }
    }
  }, [accountData])

  useEffect(() => {
    if (userCartData && userCartData.success) {
      setUserCart(userCartData?.data)
    }
  }, [userCartData])

  useEffect(() => {
    if (updateUserCartData && updateUserCartData.success) {
      userCartCall()
      raiseToast('success', 'Cart updated successfully')
    }
  }, [updateUserCartData])

  useEffect(() => {
    if (logout && logout.success) {
      setUserObj({})
      setIsLogin(false)
      localStorage.removeItem(SECURITY_SESSION_ID)
      localStorage.setItem('volume', 1)
      if (checkPrivateRoute()) {
        window.location.assign('/')
      } else {
        window.location.reload()
      }
    }
  }, [logout])

  useEffect(() => {
    setLoader(validateSessionLoading || logoutLoading || accountDataLoading)
  }, [validateSessionLoading, logoutLoading, accountDataLoading])

  useEffect(() => {
    if (validateSession && validateSession.success) {
      setUser(validateSession.data, true)
    }
  }, [validateSession])

  useEffect(() => {
    if (validateSessionError) {
      localStorage.removeItem(SECURITY_SESSION_ID)
    }
  }, [validateSessionError])

  const checkPrivateRoute = () => {
    let privateRoutesArr = [
      'favourites',
      'watchlist',
      'profile',
      'transactions',
      'subscriptions',
      'payment',
      'payment-status',
      'verify-email',
    ]

    for (let i = 0; i < privateRoutesArr.length; i++) {
      if (window.location.pathname.includes(privateRoutesArr[i])) {
        return true
      }
    }
    return false
  }

  function setUser(data, isValidatingUser = false, isSignUp = false) {
    if (
      !isValidatingUser &&
      accountObj?.od_resource_allowances?.verify_email_mandatory &&
      !data?.user?.is_email_verified
    ) {
      setVerifyOtpModalData({ title: t('verify_your_email'), email: data?.user?.email_id })
    }
    if (
      isSignUp &&
      accountObj?.od_resource_allowances?.verify_email &&
      !accountObj?.od_resource_allowances?.verify_email_mandatory &&
      !data?.user?.is_email_verified
    ) {
      setVerifyOtpModalData({ title: t('verify_your_email'), email: data?.user?.email_id })
    }
    captureGAEvent(
      'authentication',
      isSignUp ? 'register' : 'login',
      isSignUp ? 'Register' : 'Login',
      {
        user_id: data.user?._id || '',
        name: data.user?.name,
        email_id: data.user?.email_id,
      },
    )
    setUserObj(data.user)
    setIsLogin(true)
    localStorage.setItem(SECURITY_SESSION_ID, data.user_session.session_id)
  }

  function updateUserCart(productId = '', quantity = 0, variation_id = '') {
    updateUserCartCall({
      data: {
        product_id: productId,
        quantity: quantity,
        variation_id: variation_id,
      },
    })
  }

  return (
    <AppContext.Provider
      value={{
        isLogin,
        userObj,
        setUser,
        accountObj,
        setAccountObj,
        setLoader,
        setOpenLoginModal,
        openLoginModal,
        setLanguageModal,
        languageModal,
        popupModalLink,
        setPopupModalLink,
        popUpTitle,
        setPopUpTitle,
        logoutCall,
        loader,
        staticPages,
        setStaticPages,
        staticPageRoutes,
        setModalType,
        modalType,
        modulePermissions,
        setOpenForgotPasswordModal,
        openForgotPasswordModal,
        isAccountInactive,
        isCustomTemplate,
        isBrowser,
        shareModalData,
        setShareModalData,
        componentCustomForms,
        setComponentCustomForms,
        verifyOtpModalData,
        setVerifyOtpModalData,
        openAreYouStillWatching,
        setOpenAreYouStillWatching,
        lastMouseMovedSec,
        setLastMouseMovedSec,
        tipJarModalConfig,
        setTipJarModalConfig,
        userCart,
        updateUserCart,
        userCartCall,
        validateSessionCall,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}
